import { useEffect, useRef, useState } from "react";
import { X, Check } from "lucide-react";
import FieldOfWork from "components/Reusable/FieldOfWork";
import { BASE_URL } from "utils/constant";

const Input = ({ className = "", ...props }) => (
  <input
    className={`flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    {...props}
  />
);

const Label = ({ children, className = "" }) => (
  <label
    className={`block text-sm font-medium text-[#232323] mb-2 peer-disabled:cursor-not-allowed peer-disabled:opacity-70 ${className}`}
  >
    {children}
  </label>
);

export function AddContractorDialog({ open, onOpenChange, onAddContractor }) {
  const [newContractor, setNewContractor] = useState({
    name: "",
    category: "",
    workname_id: null,
    other_workname: null,
    address: "",
    email: "",
  });
  // Separate state for the raw 10-digit number (no prefix)
  const [contactNumber, setContactNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const [showFieldOfWork, setShowFieldOfWork] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [isMounted, setIsMounted] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimerRef = useRef(null);
  const isFormValid =
    Boolean(newContractor.name?.trim()) &&
    Boolean(newContractor.workname_id || newContractor.other_workname) &&
    contactNumber.length === 10;

  useEffect(() => {
    if (open) {
      setIsClosing(false);
      setShowFieldOfWork(false);
      const raf = window.requestAnimationFrame(() => setIsMounted(true));
      return () => window.cancelAnimationFrame(raf);
    }

    setIsMounted(false);

    return () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, [open]);

  const requestClose = () => {
    if (isClosing) return;
    setIsClosing(true);
    setIsMounted(false);
    closeTimerRef.current = window.setTimeout(() => {
      onOpenChange(false);
    }, 260);
  };

  // Strips non-digits, caps at 10, validates length in real time
  const handleNumberInput = (e) => {
    const onlyNums = e.target.value.replace(/[^0-9]/g, "");
    e.target.value = onlyNums;
    setContactNumber(onlyNums);
    if (onlyNums.length > 0 && onlyNums.length < 10) {
      setPhoneError("Contact number must be exactly 10 digits");
    } else {
      setPhoneError("");
    }
  };

  const handleAddContractor = async () => {
    // 10-digit guard before anything else
    if (contactNumber.length !== 10) {
      setPhoneError("Contact number must be exactly 10 digits");
      return;
    }
    setPhoneError("");

    if (
      newContractor.name &&
      (newContractor.workname_id || newContractor.other_workname)
    ) {
      setIsSubmitting(true);
      setApiError(null);
      try {
        const token = localStorage.getItem("access");
        const organization =
          JSON.parse(localStorage.getItem("organization")) || {};

        const payload = {
          organization: organization.id,
          name: newContractor.name,
          workname_id: newContractor.workname_id,
          other_workname: newContractor.other_workname,
          contact: "+91" + contactNumber, // attach prefix only for the API
          address: newContractor.address,
        };

        const response = await fetch(`${BASE_URL}/org/contractors/create/`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();
        if (data.status) {
          onAddContractor({
            ...newContractor,
            contact: "+91" + contactNumber,
            id: data.data.id,
          });
          // Reset form
          setNewContractor({
            name: "",
            category: "",
            workname_id: null,
            other_workname: null,
            address: "",
            email: "",
          });
          setContactNumber("");
          requestClose();
        } else {
          setApiError(data.message || "Failed to create contractor");
        }
      } catch (err) {
        console.error("Error creating contractor:", err);
        setApiError("An error occurred while creating contractor");
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setApiError(
        "Please fill in all required fields (Name, Category, Contact)",
      );
    }
  };

  if (!open) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-stretch justify-end bg-black/40 transition-opacity duration-300 ${isMounted && !isClosing ? "opacity-100" : "opacity-0"}`}
      onClick={requestClose}
    >
        <div
          className={`w-full sm:max-w-xl bg-white h-full flex flex-col rounded-l-xl shadow-2xl overflow-hidden transform transition-all duration-300 ease-out ${isMounted && !isClosing ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative flex items-center justify-center px-4 h-16 py-2 border-b border-gray-100 sticky top-0 bg-white z-10">
            <h3 className="text-xl font-medium text-[#232323]">
              Add New Contractor
            </h3>
            <button
              onClick={requestClose}
              className="absolute right-4 w-8 h-8 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6 pb-6">
            <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-6">
              <div className="space-y-2">
                <Label>Contractor Name *</Label>
                <Input
                  placeholder="Enter contractor name"
                  value={newContractor.name}
                  onChange={(e) =>
                    setNewContractor({ ...newContractor, name: e.target.value })
                  }
                  className="h-9"
                />
              </div>
              <div className="space-y-2">
                <Label>Category *</Label>
                <button
                  type="button"
                  onClick={() => setShowFieldOfWork(true)}
                  className="flex h-10 w-full rounded-xl border-0 bg-[#F3F4F6] px-4 py-2 text-sm text-left text-[#232323] hover:bg-gray-100"
                >
                  {newContractor.category || "Select category"}
                </button>
              </div>

              {/* ── Contact number with +91 prefix & 10-digit validation ── */}
              <div className="space-y-2">
                <Label>Contact Number *</Label>
                <div
                  className={`flex items-center bg-[#F3F4F6] px-4 py-2 rounded-xl border transition-colors ${
                    phoneError ? "border-red-500" : "border-gray-200"
                  }`}
                >
                  <span className="mr-2 text-gray-600 select-none">
                    +91
                  </span>
                  <input
                    type="text"
                    maxLength={10}
                    value={contactNumber}
                    onInput={handleNumberInput}
                    placeholder="9999999999"
                    required
                    className="w-full bg-transparent outline-none text-[#232323] placeholder:text-gray-400"
                  />
                </div>
                {phoneError && (
                  <p className="text-sm text-red-600">
                    {phoneError}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  placeholder="email@example.com"
                  value={newContractor.email}
                  onChange={(e) =>
                    setNewContractor({
                      ...newContractor,
                      email: e.target.value,
                    })
                  }
                  className="h-9"
                />
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Input
                  placeholder="Enter address"
                  value={newContractor.address}
                  onChange={(e) =>
                    setNewContractor({
                      ...newContractor,
                      address: e.target.value,
                    })
                  }
                  className="h-9"
                />
              </div>
              {apiError && (
                <p className="text-xs text-destructive font-medium">
                  {apiError}
                </p>
              )}
            </div>
          </div>
          <div className="px-6 py-4 border-t border-gray-100 bg-white flex items-center justify-center shrink-0">
            <button
              onClick={handleAddContractor}
              disabled={isSubmitting || !isFormValid}
              className={`min-w-[240px] bg-[#3B0069] hover:bg-[#3B0069D9] disabled:cursor-not-allowed text-white font-medium py-2 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 whitespace-nowrap ${
                isSubmitting || !isFormValid ? "opacity-40" : ""
              }`}
            >
              {isSubmitting ? "Adding..." : "Add Contractor"}
              <span className="rounded-full bg-[#58267F] p-1 flex items-center justify-center">
                <Check size={18} />
              </span>
            </button>
          </div>
        </div>

      {showFieldOfWork && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4"
          onClick={() => setShowFieldOfWork(false)}
        >
          <div
            className="w-full max-w-xl max-h-[90vh] overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 sticky top-0 bg-white">
               <div className="w-8" />
              <h3 className="text-xl font-medium text-[#232323]">
                Field of Work
              </h3>
              <button
                type="button"
                onClick={() => setShowFieldOfWork(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100"
                aria-label="Close field of work modal"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              <FieldOfWork
                onSelect={(field) => {
                  setNewContractor({
                    ...newContractor,
                    category: field.name,
                    workname_id: field.id,
                    other_workname: field.other_workname,
                  });
                  setShowFieldOfWork(false);
                }}
                onClose={() => setShowFieldOfWork(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
